import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { FormModal } from '@/components/shared/form-modal';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, errorMessage } from '@/lib/api';
import { useCategories, type Category } from './api';

export function CategoriesModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const q = useCategories();
  const [rows, setRows] = useState<Category[]>([]);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (q.data) setRows(q.data);
  }, [q.data]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['catalog'] });
  };
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      refresh();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const add = useMutation({
    mutationFn: () => api.post('/service-categories', { name: newName.trim(), position: rows.length }),
    onSuccess: () => {
      toast.success('Categoria salva');
      setNewName('');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const move = (i: number, dir: -1 | 1) =>
    run(async () => {
      const next = [...rows];
      [next[i], next[i + dir]] = [next[i + dir]!, next[i]!];
      await Promise.all(next.map((c, idx) => (c.position === idx ? null : api.put(`/service-categories/${c.id}`, { name: c.name, position: idx }))));
    }, 'Ordem salva');

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title="Categorias de serviços"
      description="Agrupam os serviços na agenda e no site. A ordem daqui é a ordem de exibição."
      footer={<Button onClick={() => onOpenChange(false)}>Concluir</Button>}
    >
      {q.isLoading ? (
        <LoadingState />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <div className="grid gap-4">
          <ul className="grid gap-2">
            {rows.length === 0 && <li className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">Nenhuma categoria ainda. Crie a primeira abaixo.</li>}
            {rows.map((c, i) => (
              <CategoryRow
                key={c.id}
                category={c}
                busy={busy}
                canUp={i > 0}
                canDown={i < rows.length - 1}
                onMove={(d) => move(i, d)}
                onSave={(name) => run(() => api.put(`/service-categories/${c.id}`, { name, position: c.position }), 'Categoria salva')}
                onDelete={() => run(() => api.del(`/service-categories/${c.id}`), 'Categoria excluída')}
              />
            ))}
          </ul>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (newName.trim().length >= 2) add.mutate();
            }}
          >
            <label htmlFor="new-category" className="sr-only">
              Nome da nova categoria
            </label>
            <Input id="new-category" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nova categoria" maxLength={60} />
            <Button type="submit" disabled={add.isPending || newName.trim().length < 2}>
              <Plus aria-hidden /> Adicionar categoria
            </Button>
          </form>
        </div>
      )}
    </FormModal>
  );
}

function CategoryRow({
  category,
  busy,
  canUp,
  canDown,
  onMove,
  onSave,
  onDelete,
}: {
  category: Category;
  busy: boolean;
  canUp: boolean;
  canDown: boolean;
  onMove: (d: -1 | 1) => void;
  onSave: (name: string) => void;
  onDelete: () => Promise<unknown>;
}) {
  const [name, setName] = useState(category.name);
  useEffect(() => setName(category.name), [category.name]);
  const dirty = name.trim() !== category.name && name.trim().length >= 2;
  return (
    <li className="flex items-center gap-2">
      <div className="flex flex-col">
        <Button type="button" variant="ghost" size="icon" className="size-6" disabled={!canUp || busy} onClick={() => onMove(-1)} aria-label={`Subir ${category.name}`}>
          <ArrowUp aria-hidden />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="size-6" disabled={!canDown || busy} onClick={() => onMove(1)} aria-label={`Descer ${category.name}`}>
          <ArrowDown aria-hidden />
        </Button>
      </div>
      <Input value={name} onChange={(e) => setName(e.target.value)} aria-label={`Nome da categoria ${category.name}`} maxLength={60} />
      <Button type="button" variant="outline" disabled={!dirty || busy} onClick={() => onSave(name.trim())}>
        Salvar categoria
      </Button>
      <ConfirmDialog
        trigger={
          <Button type="button" variant="ghost" size="icon" aria-label={`Excluir ${category.name}`}>
            <Trash2 className="text-destructive" aria-hidden />
          </Button>
        }
        title={`Excluir a categoria "${category.name}"?`}
        description="Os serviços dessa categoria não são apagados: ficam sem categoria."
        confirmLabel="Excluir categoria"
        onConfirm={onDelete}
      />
    </li>
  );
}
